<script setup lang="ts">
import type { Experience } from '~/types/cv'
import type { TailorSkillLine } from '~/utils/cvTailor'

/**
 * Under the skills a tailoring offered to tick: for each one ticked, an
 * optional "where did you use it?". The visitor picks the role and says in a
 * sentence what they did with it; /api/ai/skill-line turns that into one CV
 * line for the role, which they can still reword here. Only their words go
 * in: a note that does not say they used it gets a question back, not a line.
 *
 * Shared by the tailoring under a CV just built (LandingTailorDemo) and the
 * editor's (CVEditorTailorResult). Nothing shows without a role to put it under.
 */
const props = withDefaults(defineProps<{
  /** The skills ticked, in the order they were offered. */
  skills: string[]
  experiences?: Partial<Experience>[]
  /** The posting, so the line names the skill in its words. */
  posting?: string
  /** Smaller type, for the editor's side panel. */
  compact?: boolean
}>(), {
  experiences: () => [],
  posting: '',
  compact: false,
})

const lines = defineModel<TailorSkillLine[]>('lines', { required: true })

const { t } = useI18n()

/** Older CVs name the role `position`, the editor `title`. */
function roleName(index: number): string {
  const exp = props.experiences[index] as { title?: string; position?: string; company?: string } | undefined
  return [exp?.title || exp?.position, exp?.company].filter(Boolean).join(' · ') || t('skillLine.untitled')
}

interface Draft {
  index: number
  note: string
  busy: boolean
  message: string | null
}
/** The forms open, by skill. */
const drafts = reactive<Record<string, Draft>>({})

const lineFor = (skill: string) => lines.value.find(item => item.skill === skill)

function open(skill: string) {
  const current = lineFor(skill)
  drafts[skill] = { index: current?.index ?? 0, note: drafts[skill]?.note ?? '', busy: false, message: null }
}

function close(skill: string) {
  delete drafts[skill]
}

function remove(skill: string) {
  lines.value = lines.value.filter(item => item.skill !== skill)
}

function reword(skill: string, line: string) {
  lines.value = lines.value.map(item => (item.skill === skill ? { ...item, line } : item))
}

async function write(skill: string) {
  const draft = drafts[skill]
  if (!draft || draft.busy || !draft.note.trim()) return
  draft.busy = true
  draft.message = null
  const exp = props.experiences[draft.index]
  try {
    const result = await $fetch<{ ok: boolean; line: string }>('/api/ai/skill-line', {
      method: 'POST',
      body: {
        skill,
        note: draft.note.trim(),
        role: { title: exp?.title || (exp as { position?: string } | undefined)?.position, company: exp?.company, description: exp?.description },
        jobDescription: props.posting || undefined,
      },
    })
    if (!result.ok) {
      draft.message = t('skillLine.unclear', { skill })
      return
    }
    lines.value = [...lines.value.filter(item => item.skill !== skill), { skill, index: draft.index, line: result.line }]
    close(skill)
  } catch (err) {
    const status = (err as { statusCode?: number; response?: { status?: number } }).statusCode
      ?? (err as { response?: { status?: number } }).response?.status
    draft.message = t(status === 429 ? 'skillLine.limit' : 'skillLine.error')
  } finally {
    if (drafts[skill]) drafts[skill].busy = false
  }
}

// A skill unticked takes its open form with it (its line goes with the tick, in the host).
watch(() => props.skills, (skills) => {
  for (const skill of Object.keys(drafts)) if (!skills.includes(skill)) close(skill)
})
</script>

<template>
  <div v-if="skills.length && experiences.length" class="mt-4" :class="compact ? 'space-y-2' : 'space-y-3'">
    <div>
      <div class="font-title font-semibold text-ink dark:text-white" :class="compact ? '!text-xs' : 'text-sm'">{{ t('skillLine.title') }}</div>
      <p class="!text-muted dark:!text-gray-400" :class="compact ? '!text-xs' : '!text-sm'">{{ t('skillLine.hint') }}</p>
    </div>

    <div v-for="skill in skills" :key="skill" class="rounded-[0.625rem] border border-line p-3 dark:border-gray-700">
      <!-- Written: the line, under its role, still theirs to reword -->
      <template v-if="lineFor(skill) && !drafts[skill]">
        <div class="mb-1.5 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
          <span class="font-title font-semibold text-ink dark:text-white" :class="compact ? '!text-xs' : 'text-sm'">
            <i class="bi bi-check-circle-fill mr-1 text-accent" aria-hidden="true" />{{ t('skillLine.under', { skill, role: roleName(lineFor(skill)!.index) }) }}
          </span>
          <span class="flex shrink-0 gap-3">
            <button type="button" class="link !text-xs" @click="open(skill)">{{ t('skillLine.change') }}</button>
            <button type="button" class="link !text-xs" @click="remove(skill)">{{ t('skillLine.remove') }}</button>
          </span>
        </div>
        <label :for="`skill-line-${skill}`" class="sr-only">{{ t('skillLine.lineLabel', { skill }) }}</label>
        <textarea
          :id="`skill-line-${skill}`"
          :value="lineFor(skill)!.line"
          rows="2"
          maxlength="300"
          class="block w-full resize-y rounded-lg border border-accent bg-accent/5 px-3 py-2 !text-sm leading-relaxed text-ink focus:border-accent focus:outline-none focus:ring-0 dark:bg-accent/10 dark:text-white"
          @change="reword(skill, ($event.target as HTMLTextAreaElement).value)"
        />
      </template>

      <!-- Asked: which role, and what they did with it -->
      <form v-else-if="drafts[skill]" novalidate class="space-y-2" @submit.prevent="write(skill)">
        <div class="font-title font-semibold text-ink dark:text-white" :class="compact ? '!text-xs' : 'text-sm'">{{ skill }}</div>
        <div v-if="experiences.length > 1">
          <label :for="`skill-role-${skill}`" class="mb-1 block !text-xs font-semibold text-ink dark:text-white">{{ t('skillLine.role') }}</label>
          <select
            :id="`skill-role-${skill}`"
            v-model.number="drafts[skill].index"
            class="h-10 w-full rounded-lg border border-line bg-white px-3 !text-sm !text-ink focus:border-accent focus:ring-accent dark:border-gray-700 dark:bg-gray-800 dark:!text-white"
          >
            <option v-for="(_, index) in experiences" :key="index" :value="index">{{ roleName(index) }}</option>
          </select>
        </div>
        <div>
          <label :for="`skill-note-${skill}`" class="mb-1 block !text-xs font-semibold text-ink dark:text-white">{{ t('skillLine.noteLabel', { skill }) }}</label>
          <textarea
            :id="`skill-note-${skill}`"
            v-model="drafts[skill].note"
            rows="2"
            maxlength="600"
            :placeholder="t('skillLine.notePlaceholder')"
            class="block w-full resize-y rounded-lg border border-line bg-white px-3 py-2 !text-sm leading-relaxed text-ink placeholder:text-muted focus:border-accent focus:outline-none focus:ring-0 dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:placeholder:text-gray-500"
          />
        </div>
        <p v-if="drafts[skill].message" class="!text-sm text-amber-700 dark:text-amber-400" aria-live="polite">{{ drafts[skill].message }}</p>
        <div class="flex flex-wrap items-center gap-3">
          <button type="submit" class="action-pill action-pill-accent h-9 !text-xs" :disabled="drafts[skill].busy || !drafts[skill].note.trim()">
            <i class="bi" :class="drafts[skill].busy ? 'bi-arrow-repeat animate-spin' : 'bi-magic'" aria-hidden="true" />
            {{ drafts[skill].busy ? t('skillLine.writing') : t('skillLine.write') }}
          </button>
          <button type="button" class="link !text-xs" @click="close(skill)">{{ t('skillLine.cancel') }}</button>
        </div>
      </form>

      <!-- Not yet: the question, one click away -->
      <button v-else type="button" class="flex w-full items-center gap-2 text-left text-ink dark:text-white" :class="compact ? '!text-xs' : 'text-sm'" @click="open(skill)">
        <i class="bi bi-plus-circle text-accent" aria-hidden="true" />
        <span>{{ t('skillLine.add', { skill }) }}</span>
      </button>
    </div>
  </div>
</template>
